const SENSITIVE_PARAM_KEY_RE =
  /(email|e-mail|mail|name|wallet|address|payment|card|customer|user|auth|token|password|secret|phone|amount|price|receipt|transaction|tx|hash)/i;
const EMAIL_VALUE_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const WALLET_ADDRESS_VALUE_RE = /\b0x[a-fA-F0-9]{40}\b/;
const PAYMENT_CARD_VALUE_RE = /\b(?:\d[ -]*?){13,19}\b/;

const EVENT_NAMES = {
  HOMEPAGE_CTA_CLICK: "homepage_cta_click",
  LIBRARY_VIEW: "library_view",
  LIBRARY_ASSET_VIEW: "library_asset_view",
  TRDX_BUY_CLICK: "trdx_buy_click",
  NFT_COMIC_CTA_CLICK: "nft_comic_cta_click",
  OSHAR_REAL_ESTATE_VIEW: "oshar_real_estate_view",
  OSHAR_TIER_SELECT: "oshar_tier_select",
  CONNECT_WALLET_CLICK: "connect_wallet_click",
  CHECKOUT_START: "checkout_start",
  CHECKOUT_SUCCESS: "checkout_success",
  NEWSLETTER_SIGNUP: "newsletter_signup",
  SUPPORT_CLICK: "support_click",
};

function isBrowser() {
  return typeof window !== "undefined";
}

export function initializeDataLayer() {
  if (!isBrowser()) return null;

  try {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  } catch {
    return null;
  }
}

function sanitizeAnalyticsValue(value) {
  if (value == null) return value;
  if (typeof value === "string") {
    if (EMAIL_VALUE_RE.test(value) || WALLET_ADDRESS_VALUE_RE.test(value) || PAYMENT_CARD_VALUE_RE.test(value)) {
      return undefined;
    }
    return value;
  }
  if (["number", "boolean"].includes(typeof value)) return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeAnalyticsValue(item))
      .filter((item) => item == null || ["string", "number", "boolean"].includes(typeof item));
  }
  return undefined;
}

function sanitizeAnalyticsParams(params) {
  if (!params || typeof params !== "object" || Array.isArray(params)) return {};

  return Object.entries(params).reduce((safeParams, [key, value]) => {
    if (!key || SENSITIVE_PARAM_KEY_RE.test(key)) return safeParams;

    const safeValue = sanitizeAnalyticsValue(value);
    if (safeValue !== undefined) {
      safeParams[key] = safeValue;
    }
    return safeParams;
  }, {});
}

export function trackEvent(eventName, params = {}) {
  if (!isBrowser()) return false;

  try {
    const safeEventName = String(eventName || "").trim();
    if (!safeEventName) return false;

    const dataLayer = initializeDataLayer();
    if (!dataLayer || typeof dataLayer.push !== "function") return false;

    dataLayer.push({
      event: safeEventName,
      ...sanitizeAnalyticsParams(params),
    });
    return true;
  } catch {
    return false;
  }
}

export const trackHomepageCtaClick = (params = {}) =>
  trackEvent(EVENT_NAMES.HOMEPAGE_CTA_CLICK, params);

export const trackLibraryView = (params = {}) =>
  trackEvent(EVENT_NAMES.LIBRARY_VIEW, params);

export const trackLibraryAssetView = (params = {}) =>
  trackEvent(EVENT_NAMES.LIBRARY_ASSET_VIEW, params);

export const trackTrdxBuyClick = (params = {}) =>
  trackEvent(EVENT_NAMES.TRDX_BUY_CLICK, params);

export const trackNftComicCtaClick = (params = {}) =>
  trackEvent(EVENT_NAMES.NFT_COMIC_CTA_CLICK, params);

export const trackOsharRealEstateView = (params = {}) =>
  trackEvent(EVENT_NAMES.OSHAR_REAL_ESTATE_VIEW, params);

export const trackOsharTierSelect = (params = {}) =>
  trackEvent(EVENT_NAMES.OSHAR_TIER_SELECT, params);

export const trackConnectWalletClick = (params = {}) =>
  trackEvent(EVENT_NAMES.CONNECT_WALLET_CLICK, params);

export const trackCheckoutStart = (params = {}) =>
  trackEvent(EVENT_NAMES.CHECKOUT_START, params);

export const trackCheckoutSuccess = (params = {}) =>
  trackEvent(EVENT_NAMES.CHECKOUT_SUCCESS, params);

export const trackNewsletterSignup = (params = {}) =>
  trackEvent(EVENT_NAMES.NEWSLETTER_SIGNUP, params);

export const trackSupportClick = (params = {}) =>
  trackEvent(EVENT_NAMES.SUPPORT_CLICK, params);

export { EVENT_NAMES as ANALYTICS_EVENT_NAMES };
